import firebase from 'firebase/app';
import 'firebase/firestore';
import invariant from 'tiny-invariant';
import type { MachineConfig, MachineOptions } from 'xstate';
import { Machine, assign } from 'xstate';

import { Component as Share } from 'src/assets/icons/ui/share.svg';
import { BuildingModel } from 'src/modules/designStudio/database/buildingModel';
import type { ExtendedMacheteViewer } from 'src/modules/designStudio/helpers/machete';
import { takeScreenshot } from 'src/modules/designStudio/helpers/machete';
import { sharedOptions } from 'src/modules/designStudio/machines/sharedOptions';
import type { SliceOfToast } from 'src/shared/components/Toast/types';
import { ToastType } from 'src/shared/components/Toast/types';
import { MobileAppActionEnum } from 'src/shared/hooks/useReactNativeWebView/types';
import type { Translator } from 'src/shared/hooks/useTranslate';
import type { Bread } from 'src/shared/providers/Toaster';
import { ViewerVersion, createJobShare } from 'src/shared/requests';
import { withFirebaseErrorSentry } from 'src/shared/telemetry/sentry';

export const MISSING_JOBID_ERROR_MESSAGE = 'No job id was provided';

type Schema = {
  states: {
    idle: {};
    creating: {
      states: {
        screenshot: {};
        creatingJobShare: {};
        creatingShareDocument: {};
      };
    };
  };
};

export interface Context {
  shareLink: string | undefined;
  shareToken: string | undefined;
  jobId: number | null;
  userId?: number;
  viewer: ExtendedMacheteViewer | null;
  addToast: (bread: Bread) => void;
  removeToast: (toast: SliceOfToast, toasts: SliceOfToast[]) => void;
  closeMenu: () => void;
  translator: Translator;
  loading: boolean;
}

type CreateShare = { type: 'CREATE_SHARE' };
type ShareToShareSheet = { type: 'SHARE_TO_SHARE_SHEET' };
type CopyLink = { type: 'COPY_LINK' };
type SetViewer = { type: 'SET_VIEWER'; data: ExtendedMacheteViewer };
type SetShare = {
  type: 'SET_SHARE';
  data: { shareLink: string; shareToken: string };
};
type CreateJobShareEvent = {
  type: 'CREATE_JOB_SHARE';
  data: Blob;
};
type CreateShareDocumentEvent = {
  type: 'CREATE_SHARE_DOCUMENT';
  data: {
    shareLink: string | undefined;
    shareToken: string | undefined;
    userId: number | undefined;
  };
};

export type Event =
  | ShareToShareSheet
  | CopyLink
  | CreateShare
  | SetShare
  | SetViewer
  | CreateJobShareEvent
  | CreateShareDocumentEvent;

interface MobileAppEventPayload {
  action: MobileAppActionEnum;
  data?: string;
}

const sendEventToMobileApp = (payload: MobileAppEventPayload) => {
  try {
    window.ReactNativeWebView?.postMessage(JSON.stringify(payload));
  } catch (e: any) {
    // See DS-846
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    sharedOptions.logError(undefined, e);
  }
};

const copyLinkViaBrowserAPI = async (shareLink: string) => {
  try {
    // uses the browser api
    await navigator.clipboard.writeText(shareLink);
  } catch (e: any) {
    // See DS-846
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    sharedOptions.logError(undefined, e);
  }
};

export const options: MachineOptions<Context, Event> = {
  actions: {
    closeMenu: ctx => ctx.closeMenu(),
    addCopiedLinkToast: ctx =>
      ctx.addToast({
        title: ctx.translator('share.shareSuccessful'),
        subtitle: ctx.translator('share.copiedLink'),
        icon: Share,
        onPress: ctx.removeToast,
        type: ToastType.ACTIVE,
      }),
    copyLink: async ctx => {
      if (ctx.shareLink) {
        await copyLinkViaBrowserAPI(ctx.shareLink);
        // also send event to RN to handle
        sendEventToMobileApp({
          action: MobileAppActionEnum.COPY_LINK,
          data: ctx.shareLink,
        });
      }
    },
    beginLoadingState: assign(ctx => ({ ...ctx, loading: true as boolean })),
    endLoadingState: assign(ctx => ({ ...ctx, loading: false as boolean })),
    logError: sharedOptions.logError,
    setShareData: assign((_, evt) => (evt as SetShare).data),
    setViewer: assign((_, evt) => ({ viewer: (evt as SetViewer).data })),
    shareSheet: ctx => {
      const payload = {
        action: MobileAppActionEnum.SHARE,
        data: ctx.shareLink,
      };
      // send event to RN to handle
      sendEventToMobileApp(payload);
    },
  },
  guards: {},
  services: {
    takeScreenShot: async (ctx): Promise<Blob> => {
      const screenshot: Blob = await takeScreenshot(ctx.viewer);

      return screenshot;
    },
    createJobShare: async (ctx: Context, e) => {
      invariant(ctx.jobId, MISSING_JOBID_ERROR_MESSAGE);
      const { data: imageBlob } = e as CreateJobShareEvent;
      const { jobId } = ctx;

      const jobShare = await createJobShare(
        jobId,
        ViewerVersion.DESIGN_STUDIO,
        imageBlob,
      );

      return {
        shareLink: jobShare?.url,
        shareToken: jobShare?.token,
        // ctx.userId is proView, jobShare.user_id is current user
        userId: ctx.userId || jobShare?.user_id,
      };
    },
    createShareDocument: async (ctx, e) => {
      const { data } = e as CreateShareDocumentEvent;
      invariant(ctx.jobId, MISSING_JOBID_ERROR_MESSAGE);

      const buildingModel = new BuildingModel(ctx.jobId);
      const modelRef = await buildingModel.getBuildingModelReference();

      return withFirebaseErrorSentry(() =>
        modelRef?.collection('shares').doc(data.shareToken).set({
          author_id: data.userId,
          created_at: firebase.firestore?.Timestamp.now(),
        }),
      )();
    },
  },
  delays: {},
  activities: {},
};

export const machineConfig: MachineConfig<Context, Schema, Event> = {
  initial: 'idle',
  context: {
    shareLink: undefined,
    shareToken: undefined,
    jobId: null,
    viewer: null,
    addToast: () => undefined,
    removeToast: () => undefined,
    closeMenu: () => undefined,
    translator: () => '',
    loading: false,
  },
  on: { SET_VIEWER: { actions: 'setViewer' } },
  states: {
    idle: {
      id: 'idle',
      on: {
        CREATE_SHARE: { target: 'creating', actions: ['beginLoadingState'] },
        COPY_LINK: {
          actions: ['copyLink', 'addCopiedLinkToast', 'closeMenu'],
        },
        SHARE_TO_SHARE_SHEET: {
          actions: ['shareSheet'],
        },
      },
    },
    creating: {
      initial: 'screenshot',
      states: {
        screenshot: {
          invoke: {
            src: 'takeScreenShot',
            onDone: 'creatingJobShare',
            onError: {
              target: '#idle',
              actions: ['logError', 'endLoadingState'],
            },
          },
        },
        creatingJobShare: {
          invoke: {
            src: 'createJobShare',
            onDone: {
              target: 'creatingShareDocument',
              actions: ['setShareData'],
            },
            onError: {
              target: '#idle',
              actions: ['logError', 'endLoadingState'],
            },
          },
        },
        creatingShareDocument: {
          invoke: {
            src: 'createShareDocument',
            onDone: { target: '#idle', actions: ['endLoadingState'] },
            onError: {
              target: '#idle',
              actions: ['logError', 'endLoadingState'],
            },
          },
        },
      },
    },
  },
};

export const createMachine = (initialContext: Context) =>
  Machine<Context, Schema, Event>(machineConfig, options, initialContext);
